import { apiRequest } from '@/integrations/api/client';
import type { AppRole } from '@/integrations/api/auth_api';

export interface TenantInvitationDto {
  id: string;
  tenant_id?: string;
  tenantId?: string;
  email: string;
  role: AppRole;
  status: string;
  created_at?: string;
  createdAt?: string;
  expires_at?: string;
  expiresAt?: string;
  accepted_at?: string | null;
  invited_by?: string;
  invitedBy?: string;
}

export interface TenantMemberDto {
  id: string;
  user_id: string;
  role: AppRole;
  is_active: boolean;
  email: string | null;
  full_name: string | null;
}

export async function fetchMyOrganizations() {
  const res = await apiRequest<{
    organizations: Array<{
      id: string;
      name: string;
      slug: string;
      role: AppRole;
      plan: string;
    }>;
  }>('/api/tenants/mine');
  return res.organizations;
}

export async function createOrganization(input: { name: string; slug?: string }) {
  const res = await apiRequest<{
    organization: {
      id: string;
      name: string;
      slug: string;
      role: AppRole;
      plan: string;
    };
  }>('/api/tenants', {
    method: 'POST',
    body: input,
  });
  return res.organization;
}

export async function listMembers(tenantId: string) {
  const res = await apiRequest<{ members: TenantMemberDto[] }>(
    `/api/tenants/${tenantId}/members`,
    { tenantId },
  );
  return res.members;
}

export async function updateMember(
  tenantId: string,
  memberId: string,
  patch: { role?: AppRole; is_active?: boolean },
) {
  const res = await apiRequest<{ member: TenantMemberDto }>(
    `/api/tenants/${tenantId}/members/${memberId}`,
    {
      method: 'PATCH',
      tenantId,
      body: patch,
    },
  );
  return res.member;
}

export async function listInvitations(tenantId: string) {
  const res = await apiRequest<{ invitations: TenantInvitationDto[] }>(
    `/api/tenants/${tenantId}/invitations`,
    { tenantId },
  );
  return res.invitations.map(normalizeInvitation);
}

export async function createInvitation(input: {
  tenant_id: string;
  email: string;
  role: AppRole;
}) {
  const res = await apiRequest<{
    invitation: TenantInvitationDto;
    invite_url: string;
  }>(`/api/tenants/${input.tenant_id}/invitations`, {
    method: 'POST',
    tenantId: input.tenant_id,
    body: { email: input.email, role: input.role },
  });
  return {
    invitation_id: res.invitation.id,
    expires_at: res.invitation.expires_at ?? res.invitation.expiresAt ?? '',
    invite_link: res.invite_url,
  };
}

export async function revokeInvitation(tenantId: string, invitationId: string) {
  await apiRequest(`/api/tenants/${tenantId}/invitations/${invitationId}`, {
    method: 'DELETE',
    tenantId,
  });
}

export async function previewInvitation(token: string) {
  const res = await apiRequest<{
    valid: boolean;
    status: 'pending' | 'accepted' | 'revoked' | 'expired';
    email: string;
    role: AppRole;
    tenant: { id: string; name: string; slug: string };
    expires_at: string;
    invitation_token_hash: string;
  }>('/api/invitations/accept', {
    method: 'POST',
    auth: false,
    body: { token },
  });
  return {
    email: res.email,
    role: res.role,
    tenant_id: res.tenant.id,
    tenant_name: res.tenant.name,
    status: res.status,
    expires_at: res.expires_at,
    invitation_token_hash: res.invitation_token_hash,
  };
}

export async function applyInvitation(token: string) {
  const res = await apiRequest<{
    tenant_id: string;
    tenant_name: string;
    role: AppRole;
    already_member?: boolean;
  }>('/api/invitations/apply', {
    method: 'POST',
    body: { token },
  });
  return {
    tenant_id: res.tenant_id,
    tenant_name: res.tenant_name,
    role: res.role,
  };
}

export async function fetchMyPermissions(tenantId: string) {
  const res = await apiRequest<{ permissions: string[] }>('/api/tenants/permissions/mine', {
    tenantId,
  });
  return new Set(res.permissions);
}

function normalizeInvitation(row: TenantInvitationDto) {
  return {
    id: row.id,
    tenant_id: row.tenant_id ?? row.tenantId ?? '',
    email: row.email,
    role: row.role,
    status: row.status as 'pending' | 'accepted' | 'revoked' | 'expired',
    created_at: row.created_at ?? row.createdAt ?? '',
    expires_at: row.expires_at ?? row.expiresAt ?? '',
    accepted_at: row.accepted_at ?? null,
    invited_by: row.invited_by ?? row.invitedBy ?? '',
  };
}
